/*
 * pure request builders, close selection rules and the Slack-digest deep-link parser for the Needs
 * attention tab.
 */
import { ist_day } from '../shared/ist';

// issues.TierOrder — the display order, top to bottom.
export const TIERS = [
  'ERRORS',
  'MUST_REVIEW',
  'NEEDS_ATTENTION',
  'PROBABLE_ISSUES',
  'SETUP_GAPS',
  'CHANGED_AFTER_REVIEW',
  'INFO'
];

export const TIER_LABELS = {
  ERRORS: 'Errors',
  MUST_REVIEW: 'Must review',
  NEEDS_ATTENTION: 'Needs attention',
  PROBABLE_ISSUES: 'Probable issues',
  SETUP_GAPS: 'Setup gaps',
  CHANGED_AFTER_REVIEW: 'Changed after review',
  INFO: 'For information'
};

// The backend's default filter when `status` is not sent is OPEN + ACKNOWLEDGED.
export const ISSUE_STATUSES = ['OPEN', 'ACKNOWLEDGED', 'RESOLVED', 'AUTO_CLEARED'];

// the statuses an Ops decision leaves behind (AUTO_CLEARED is the system). The list accepts them as
// one CSV filter; the server names it as the full set behind "Recent Ops decisions".
export const DECIDED_STATUSES = ['ACKNOWLEDGED', 'RESOLVED'];
export const DECIDED_STATUS_FILTER = DECIDED_STATUSES.join(',');
export const DECIDED_STATUS_LABEL = 'Acknowledged + resolved';

export const ACTION_ACKNOWLEDGE = 'ACKNOWLEDGE';
export const ACTION_RESOLVE = 'RESOLVE';
export const ISSUE_ACTIONS = [ACTION_ACKNOWLEDGE, ACTION_RESOLVE];

// Resolving a QA review row clears QA for the session (goserver PROD-10210): Ops see "QA cleared".
export const QA_REVIEW_STATUS = 'QA_REVIEW_STATUS';
export const resolve_label = (issue_code) =>
  issue_code === QA_REVIEW_STATUS ? 'QA cleared' : 'Resolve';

// QA cleared clears only the QA check (Ops/Nithun, 1 Nov 2026): every other issue on the class stays
// open and blocks pay until reviewed on its own.
export const QA_OTHERS_NOTE = 'Clearing QA does not clear these; each needs its own review.';
export const QA_CLEARED_RESULT_TEXT = 'Now QA cleared. Other issues on these classes stay open.';

// goserver qaReviewDetail: "<reason> Also: <label>; <label>" (reason may be empty).
const QA_ALSO_SEP = ' Also: ';
export const qa_detail_parts = (issue_code, detail) => {
  const text = String(detail || '').trim();
  if (issue_code !== QA_REVIEW_STATUS) return { reason: text, labels: [] };
  const at = ` ${text}`.indexOf(QA_ALSO_SEP);
  if (at === -1) return { reason: text, labels: [] };
  const labels = ` ${text}`
    .slice(at + QA_ALSO_SEP.length)
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean);
  return { reason: text.slice(0, Math.max(at - 1, 0)).trim(), labels };
};

// "Also on this class: A, B" for the given labels (deduped, in order); '' when none.
export const qa_others_text = (labels) => {
  const seen = [];
  (Array.isArray(labels) ? labels : []).forEach((l) => {
    if (l && seen.indexOf(l) === -1) seen.push(l);
  });
  return seen.length ? `Also on this class: ${seen.join(', ')}` : '';
};

// KIND_OUT_OF_SCOPE's month row lists its classes ("2 classes: <IST start> <uuid>, ..."): the count,
// then one class per line. Any other detail is one line, as sent.
export const KIND_OUT_OF_SCOPE = 'KIND_OUT_OF_SCOPE';
const CLASS_LIST = /^(\d+ class(?:es)?): (.+)$/;
export const detail_lines = (issue_code, detail) => {
  const text = String(detail || '').trim();
  if (!text) return [];
  const m = issue_code === KIND_OUT_OF_SCOPE ? text.match(CLASS_LIST) : null;
  if (m) return [m[1], ...m[2].split(', ')];
  // a QA row's folded labels render as their own "Also on this class" block
  const { reason } = qa_detail_parts(issue_code, text);
  return reason ? [reason] : [];
};

// The pop-up's verb: "QA cleared" only when every row it acts on is a QA review row.
export const action_verb = (action, rows) => {
  if (action === ACTION_ACKNOWLEDGE) return 'Acknowledge';
  const list = Array.isArray(rows) ? rows : [];
  return list.length && list.every((r) => r.issue_code === QA_REVIEW_STATUS)
    ? 'QA cleared'
    : 'Resolve';
};

export const ISSUE_SORTS = [
  'tier_amount',
  'amount',
  'first_seen',
  'last_seen',
  'teacher_name',
  'issue',
  'scope',
  'session_time',
  'status'
];
export const SORT_DIRS = ['asc', 'desc'];
export const DEFAULT_SORT = 'tier_amount';
export const DEFAULT_DIR = 'desc';

// issues/registry.go stake_rule NONE — a code with no pay impact; its amount_at_stake is always 0, so
// the UI shows "—" there instead of "₹0" (which would read as a real, computed zero stake).
export const NONE_STAKE_ISSUE_CODES = [
  'BAD_MONTH',
  'MONTH_LIFECYCLE_UNEXPECTED',
  'NEXT_MONTH_CLOSED_FIRST',
  'NO_MONTH_ROW',
  'PREV_MONTH_NOT_CLOSED',
  'SESSION_BEFORE_FIRST_PAID_MONTH',
  'SESSION_NOT_HELD_DROPPED',
  'ZOOM_ROW_BOTH_IDS',
  'ZOOM_ROW_UNATTRIBUTED',
  'ANOMALY_MANY_REPLACEMENTS',
  'SETUP_NO_OPENING_BALANCE',
  'FREE_PASS_PENDING_D24',
  'MONTH_NOT_OPEN',
  'MONTH_REOPENED',
  'NEW_TEACHER_FIRST_MONTH',
  'TEACHER_MID_SESSION_DROPOUT',
  'PROFILE_LIVE_AT_BLANK',
  'CLOSED_MONTH_CHANGED_TOO_OLD',
  'GC_NO_STUDENTS_JOINED',
  'EMPLOYEE_SESSION',
  'KIND_OUT_OF_SCOPE'
];
// Labels for codes a server may send before its catalogue row reaches the synced goldens (A-52);
// the server's own label and suggested action always win.
export const REQUEST_TIME_BEFORE_BOOKING = 'REQUEST_TIME_BEFORE_BOOKING';
export const PAY_INPUT_ACKNOWLEDGED = 'PAY_INPUT_ACKNOWLEDGED';
// an id-less 1-on-1 Zoom join matching neither name, counted as the student (option c, 1 Nov 2026)
export const ZOOM_PARTICIPANT_UNRECOGNISED = 'ZOOM_PARTICIPANT_UNRECOGNISED';
export const ISSUE_CODE_LABELS = {
  [REQUEST_TIME_BEFORE_BOOKING]: 'Request time is before the class was booked',
  [PAY_INPUT_ACKNOWLEDGED]: 'Pay-affecting date or time saved over a warning',
  [ZOOM_PARTICIPANT_UNRECOGNISED]: 'Unrecognised Zoom participant'
};
export const ISSUE_CODE_ACTIONS = {
  [REQUEST_TIME_BEFORE_BOOKING]:
    'Check the request time with the teacher or student; correct it in the class, or resolve with a reason.',
  [PAY_INPUT_ACKNOWLEDGED]:
    "Check the change and the note against the class or the teacher's records; correct it, or resolve with a reason.",
  [ZOOM_PARTICIPANT_UNRECOGNISED]:
    'Check who joined (recording/Zoom names). Keep the class, or change its status; pay re-prices.'
};
export const issue_code_label = (issue_code) => ISSUE_CODE_LABELS[issue_code] || issue_code || '';
export const issue_code_action = (issue_code) => ISSUE_CODE_ACTIONS[issue_code] || '';

export const has_no_stake = (issue_code) => NONE_STAKE_ISSUE_CODES.indexOf(issue_code) !== -1;

// a still-live "earned a Free Pass Day, no finished class" row offers removing the pass.
export const FREE_PASS_NO_FINISHED_CLASS = 'FREE_PASS_NO_FINISHED_CLASS';
export const offers_month_pass_removal = (row) =>
  !!row && row.issue_code === FREE_PASS_NO_FINISHED_CLASS && row.selectable === true;

export const READINESS_STATES = ['READY', 'BLOCKED', 'CLOSED'];

// a BLOCKED readiness row for a teacher with an open blocking issue but no month row to close.
export const NO_MONTH_ROW_LIFECYCLE = 'NO_MONTH_ROW';
export const DEFAULT_READINESS_STATE = 'READY';

// the second reason covers MUST_REVIEW only — an open ERRORS issue is fixed, never overridden.
// Mirrors overridableTiers in needs_attention_types.go.
export const OVERRIDABLE_TIERS = ['MUST_REVIEW'];

// Guardrail: every close/reopen blocker code goserver returns (needs_attention_types.go), each
// named with whether the override reason can release it — only CLOSE_BLOCKED_ISSUES can
// (close_plan.go). A new server code fails params.test.js until it is handled here.
export const BLOCKER_OVERRIDABLE = {
  CLOSE_BLOCKED_ERRORS: false,
  CLOSE_BLOCKED_ISSUES: true,
  CLOSE_PREV_MONTH_OPEN: false,
  CLOSE_NOT_OPEN: false,
  // the teacher has no payment record this month; there is nothing to close.
  CLOSE_NO_MONTH_ROW: false,
  CLOSE_GRANT_MISMATCH: false,
  CLOSE_GRANT_GATED: false,
  CLOSE_COMPUTE_FAILED: false,
  // The grant write failed and the close wrote nothing; a plain re-close retries it.
  CLOSE_GRANT_FAILED: false,
  // a later month closed first; only reopening it frees this one, never an override.
  CLOSE_NEXT_MONTH_CLOSED: false,
  CLOSE_RECALC_RACE: false,
  // the COMMIT errored and the re-read could not show the close landed; it may have been saved.
  CLOSE_OUTCOME_UNKNOWN: false,
  // writing the carry-forward failed and the whole close rolled back; a re-close retries it.
  CLOSE_CARRY_FORWARD_FAILED: false,
  // Months before the system's first paid month (2026-10) are paid outside it; never closable.
  CLOSE_BEFORE_SYSTEM_START: false,
  REOPEN_LATER_MONTH_CLOSED: false,
  MONTH_NOT_CLOSED: false,
  // the reopen twin of CLOSE_OUTCOME_UNKNOWN.
  REOPEN_OUTCOME_UNKNOWN: false,
  // reversing the carry-forward failed; the month stays closed and nothing changed.
  REOPEN_CARRY_FORWARD_FAILED: false,
  // this month's bank line is in a file or held; settle it in Payouts first.
  REOPEN_PAYOUT_IN_FLIGHT: false
};
export const BLOCKER_CODES = Object.keys(BLOCKER_OVERRIDABLE);

// a close or reopen that may or may not have been saved. The refreshed list is the truth.
const OUTCOME_UNKNOWN_CODES = ['CLOSE_OUTCOME_UNKNOWN', 'REOPEN_OUTCOME_UNKNOWN'];

export const is_outcome_unknown = (row) =>
  !!row &&
  (Array.isArray(row.blockers) ? row.blockers : []).some(
    (b) => !!b && OUTCOME_UNKNOWN_CODES.indexOf(b.code) !== -1
  );

// no payment record this month. The server says so by lifecycle AND by blocker; either is enough.
export const is_no_month_row = (row) =>
  !!row &&
  (row.lifecycle === NO_MONTH_ROW_LIFECYCLE ||
    (Array.isArray(row.blockers) ? row.blockers : []).some(
      (b) => !!b && b.code === 'CLOSE_NO_MONTH_ROW'
    ));

// Both halves must agree: a hard code wrongly flagged overridable, or a code with no handler yet, stays
// hard — the batch would otherwise be sent with an override that cannot release it.
export const is_blocker_overridable = (b) =>
  !!b && b.overridable === true && BLOCKER_OVERRIDABLE[b.code] === true;

// an ERRORS issue is fixed, never acknowledged (registry: every ERRORS entry has
// acknowledgeable=false).
const NEVER_ACKNOWLEDGED_TIER = 'ERRORS';

export const is_acknowledgeable = (row) =>
  !!row && row.acknowledgeable === true && row.tier !== NEVER_ACKNOWLEDGED_TIER;

// needs_attention_types.go: maxCloseTeachers (the per-request /month/close, kept one release beside
// the runs) / maxIssueActionIDs / maxNeedsAttentionReasonLen.
export const MAX_CLOSE_TEACHERS = 10;
export const MAX_ISSUE_ACTION_IDS = 100;
export const MAX_REASON_LEN = 1024;
// needs_attention_types.go recentDecisionsLimit: the summary panel is a preview, the list is the record.
export const RECENT_DECISIONS_LIMIT = 10;
// The backend accepts 1 character; the house rule for payment-impacting reasons is 3
// (required_reason_textarea/reason_rules.js), so the UI is deliberately the stricter of the two.
export const MIN_REASON_LEN = 3;

export const VIEW_MONTHLY = 'monthly';
export const VIEW_DAILY = 'daily';

// The daily window the backend will serve: today IST back to 90 days.
export const MAX_DAILY_LOOKBACK_DAYS = 90;

const PAY_MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
// Registry codes are UPPER_SNAKE, VARCHAR(64); anything else is never sent (the backend would 400).
export const ISSUE_CODE_RE = /^[A-Z0-9_]{1,64}$/;
export const UUID_RE = /^[A-Za-z0-9-]{1,64}$/;

const in_list = (list, value) => list.indexOf(value) !== -1;

// ---- query params ----

// Unknown values are dropped rather than sent: the backend answers 400 on an unknown tier/sort/status,
// and a stale bookmark must degrade to "no filter", never to an error page.
export const status_csv = (statuses) => {
  const list = Array.isArray(statuses) ? statuses : String(statuses || '').split(',');
  return list
    .map((s) => String(s || '').trim())
    .filter((s) => in_list(ISSUE_STATUSES, s))
    .join(',');
};

export const summary_params = (pay_month) => ({ pay_month });

// the categories of one tier: an unknown tier is sent empty, which the server refuses (BAD_TIER).
export const categories_params = (pay_month, tier) => ({
  pay_month,
  tier: in_list(TIERS, tier) ? tier : ''
});

export const issues_params = (filters, limit, offset) => {
  const f = filters || {};
  return {
    pay_month: f.pay_month,
    tier: in_list(TIERS, f.tier) ? f.tier : '',
    issue_code: ISSUE_CODE_RE.test(f.issue_code || '') ? f.issue_code : '',
    teacher_uuid: UUID_RE.test(f.teacher_uuid || '') ? f.teacher_uuid : '',
    status: status_csv(f.status),
    sort: in_list(ISSUE_SORTS, f.sort) ? f.sort : DEFAULT_SORT,
    dir: in_list(SORT_DIRS, f.dir) ? f.dir : DEFAULT_DIR,
    limit,
    offset
  };
};

// The readiness `q` (name or email substring): the server trims it and allows 100 characters.
export const MAX_TEACHER_SEARCH_LEN = 100;
export const TEACHER_SEARCH_TOO_LONG_TEXT = `Search with at most ${MAX_TEACHER_SEARCH_LEN} characters of a name or email`;

const teacher_search = (q) => String(q === null || q === undefined ? '' : q).trim();

// a longer search is refused at the field and never sent, rather than silently searching a cut
// prefix; BAD_QUERY stays the server's backstop. Characters (code points), as the server counts
// runes.
export const teacher_search_error = (q) =>
  Array.from(teacher_search(q)).length > MAX_TEACHER_SEARCH_LEN ? TEACHER_SEARCH_TOO_LONG_TEXT : '';

export const readiness_params = (filters, limit, offset) => {
  const f = filters || {};
  return {
    pay_month: f.pay_month,
    state: in_list(READINESS_STATES, f.state) ? f.state : DEFAULT_READINESS_STATE,
    q: teacher_search(f.q),
    limit,
    offset
  };
};

export const daily_params = (day) => ({ day: DAY_RE.test(day || '') ? day : '' });

// ---- IST day arithmetic (the daily window is an IST calendar day) ----

export const shift_day = (day, days) => {
  if (!DAY_RE.test(day || '')) return '';
  const t = Date.parse(`${day}T00:00:00Z`);
  if (!Number.isFinite(t)) return '';
  return new Date(t + days * 86400000).toISOString().slice(0, 10);
};

// The backend 400s (BAD_DAY) on a future day or one older than 90 days, so neither is ever sent.
export const accept_day = (day, today) => {
  if (!DAY_RE.test(day || '') || !DAY_RE.test(today || '')) return false;
  return day <= today && day >= shift_day(today, -MAX_DAILY_LOOKBACK_DAYS);
};

// Both day arrows are disabled at the edges of that window rather than clicking to nothing.
export const can_shift_day = (day, days, today) => accept_day(shift_day(day, days), today);

// deep links ---- MONTHLY:
// /teacher-payments/needs-attention?view=monthly&pay_month=YYYY-MM[&tier=TIER][&teacher=UUID]
// (teacher_uuid= is the legacy name, still read).
// DAILY: /teacher-payments/needs-attention?view=daily&day=YYYY-MM-DD

export const parse_needs_attention_deep_link = (search, today = ist_day()) => {
  const qs = new URLSearchParams(typeof search === 'string' ? search : '');
  const view = (qs.get('view') || '').trim().toLowerCase();
  const pay_month = (qs.get('pay_month') || '').trim();
  const tier = (qs.get('tier') || '').trim().toUpperCase();
  const day = (qs.get('day') || '').trim();
  const teacher_uuid = (qs.get('teacher') || qs.get('teacher_uuid') || '').trim();
  return {
    view: view === VIEW_DAILY ? VIEW_DAILY : VIEW_MONTHLY,
    pay_month: PAY_MONTH_RE.test(pay_month) ? pay_month : '',
    tier: in_list(TIERS, tier) ? tier : '',
    day: accept_day(day, today) ? day : '',
    // A teacher search link opens straight to that teacher's issues (every tier), like a run row's
    // "View issues"; tier stays whatever the link also named, or every tier if it named none.
    teacher_uuid: UUID_RE.test(teacher_uuid) ? teacher_uuid : ''
  };
};

// The daily report carries `monthly_link` as a relative path; the page follows it in place rather than
// reloading, so it is parsed with the same whitelists as the URL.
export const parse_link_target = (link, today = ist_day()) => {
  const raw = typeof link === 'string' ? link : '';
  const q = raw.indexOf('?');
  return parse_needs_attention_deep_link(q === -1 ? '' : raw.slice(q + 1), today);
};

// ---- mandatory reasons (every Ops action carries one) ----

export const trim_reason = (raw) => String(raw === null || raw === undefined ? '' : raw).trim();

// characters (code points), as the server counts runes — not units.
export const reason_length = (raw) => Array.from(trim_reason(raw)).length;

export const reason_error = (raw) => {
  const n = reason_length(raw);
  if (n === 0) return 'A reason is required.';
  if (n < MIN_REASON_LEN) return `The reason must be at least ${MIN_REASON_LEN} characters.`;
  if (n > MAX_REASON_LEN) return `The reason must be at most ${MAX_REASON_LEN} characters.`;
  return '';
};

// ---- issue actions ----

const issue_ids_of = (ids) =>
  (Array.isArray(ids) ? ids : [])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0)
    .filter((id, i, arr) => arr.indexOf(id) === i);

// An ERRORS-tier issue cannot be ACKNOWLEDGED (the backend refuses with ISSUE_TIER_NOT_ACKNOWLEDGEABLE);
// RESOLVED/AUTO_CLEARED rows are never re-actioned. Filtering here
// keeps the request honest and lets the modal tell Ops up front how many of its selection will be skipped.
export const is_issue_live = (row) =>
  !!row && (row.status === 'OPEN' || row.status === 'ACKNOWLEDGED');

export const issue_applies = (row, action) => {
  if (!is_issue_live(row)) return false;
  if (action === ACTION_ACKNOWLEDGE) return is_acknowledgeable(row) && row.status === 'OPEN';
  return action === ACTION_RESOLVE;
};

export const applicable_issue_ids = (rows, ids, action) => {
  const wanted = issue_ids_of(ids);
  return (Array.isArray(rows) ? rows : [])
    .filter((r) => wanted.indexOf(Number(r.id)) !== -1 && issue_applies(r, action))
    .map((r) => Number(r.id));
};

// A retry is safe: the server skips a row already closed and says so.
export const refreshed_issue_rows = (sent_rows, fresh_rows) => {
  const fresh = Array.isArray(fresh_rows) ? fresh_rows : [];
  return (Array.isArray(sent_rows) ? sent_rows : []).map(
    (r) => fresh.find((f) => Number(f.id) === Number(r.id)) || r
  );
};

// "Select all on page": only rows that can be ticked one by one.
const selectable_ids = (rows) =>
  (Array.isArray(rows) ? rows : []).filter((r) => r && r.selectable).map((r) => r.id);

export const page_selection_state = (selected, rows) => {
  const ids = selectable_ids(rows);
  const picked = ids.filter((id) => selected.indexOf(id) !== -1).length;
  return {
    count: ids.length,
    all: ids.length > 0 && picked === ids.length,
    some: picked > 0 && picked < ids.length
  };
};

// All ticked: untick this page's rows; otherwise tick every selectable row, keeping order.
export const toggle_page_selection = (selected, rows) => {
  const ids = selectable_ids(rows);
  if (page_selection_state(selected, rows).all)
    return selected.filter((id) => ids.indexOf(id) === -1);
  return selected.concat(ids.filter((id) => selected.indexOf(id) === -1));
};

export const issue_action_payload = (issue_ids, action, reason) => ({
  issue_ids: issue_ids_of(issue_ids).slice(0, MAX_ISSUE_ACTION_IDS),
  action: in_list(ISSUE_ACTIONS, action) ? action : '',
  reason: trim_reason(reason)
});

// ---- month close / reopen ----

// A teacher-month can be put in a close run when the backend already calls it closable, or when
// every blocker it carries is overridable (second reason covers exactly those).
export const is_selectable_for_close = (row) => {
  if (!row || is_no_month_row(row)) return false;
  if (row.closable === true) return true;
  const blockers = Array.isArray(row.blockers) ? row.blockers : [];
  return blockers.length > 0 && blockers.every(is_blocker_overridable);
};

export const selectable_close_uuids = (rows) =>
  (Array.isArray(rows) ? rows : []).filter(is_selectable_for_close).map((r) => r.teacher_uuid);

// the second reason is demanded as soon as ONE selected teacher has an overridable blocker open —
// the backend refuses the whole run (requires_override, nothing created) otherwise.
export const needs_override_reason = (rows, selected_uuids) => {
  const wanted = Array.isArray(selected_uuids) ? selected_uuids : [];
  return (Array.isArray(rows) ? rows : []).some(
    (r) =>
      r &&
      wanted.indexOf(r.teacher_uuid) !== -1 &&
      (Array.isArray(r.blockers) ? r.blockers : []).some(is_blocker_overridable)
  );
};

const not_retried = (r) =>
  r.outcome === 'CLOSED' ||
  r.lifecycle === 'CLOSED' ||
  is_no_month_row(r) ||
  is_outcome_unknown(r) ||
  (r.precheck === true &&
    (Array.isArray(r.blockers) ? r.blockers : []).some((b) => !is_blocker_overridable(b)));

export const retry_close_uuids = (selected_uuids, results) => {
  const dropped = (Array.isArray(results) ? results : [])
    .filter((r) => r && not_retried(r))
    .map((r) => r.teacher_uuid);
  return (Array.isArray(selected_uuids) ? selected_uuids : []).filter(
    (uuid) => uuid && dropped.indexOf(uuid) === -1
  );
};

// filter_count: "Select all N matching" stands in for a ticked list.
export const close_form_error = ({
  selected_uuids,
  filter_count = 0,
  reason,
  override_reason,
  requires_override
}) => {
  const picked = Array.isArray(selected_uuids) ? selected_uuids.length : 0;
  if (picked === 0 && !(filter_count > 0)) {
    return 'Select at least one teacher to close.';
  }
  const reason_msg = reason_error(reason);
  if (reason_msg) return reason_msg;
  if (requires_override) {
    const override_msg = reason_error(override_reason);
    if (override_msg) {
      return override_msg === 'A reason is required.'
        ? 'An override reason is required: some selected teachers still have open must-review issues.'
        : override_msg;
    }
  }
  return '';
};

// The per-request /month/close and /month/reopen stay live for one release; the dialogs start runs
// instead, and request_fields.test.js keeps these builders pinned to the old handlers.
export const close_payload = (pay_month, teacher_uuids, reason, override_reason) => ({
  pay_month,
  teacher_uuids: (Array.isArray(teacher_uuids) ? teacher_uuids : []).slice(0, MAX_CLOSE_TEACHERS),
  reason: trim_reason(reason),
  override_reason: trim_reason(override_reason)
});

export const reopen_payload = (pay_month, teacher_uuid, reason) => ({
  pay_month,
  teacher_uuid: teacher_uuid || '',
  reason: trim_reason(reason)
});

export const reopen_form_error = ({ selected_count, reason }) => {
  if (!(selected_count > 0)) return 'Select the teachers whose month you are reopening.';
  return reason_error(reason);
};

// The issue list's Sort select: every sort in both directions ("key|dir"); the default order is the
// select's own blank option.
export const ISSUE_SORT_OPTIONS = [
  { value: 'amount|desc', label: '₹ at stake, highest first' },
  { value: 'amount|asc', label: '₹ at stake, lowest first' },
  { value: 'session_time|desc', label: 'Session date, newest first' },
  { value: 'session_time|asc', label: 'Session date, oldest first' },
  { value: 'first_seen|desc', label: 'First found by system, newest first' },
  { value: 'first_seen|asc', label: 'First found by system, oldest first' },
  { value: 'last_seen|desc', label: 'Last found by system, newest first' },
  { value: 'last_seen|asc', label: 'Last found by system, oldest first' },
  { value: 'teacher_name|asc', label: 'Teacher name, A to Z' },
  { value: 'teacher_name|desc', label: 'Teacher name, Z to A' }
];

export const sort_option_value = (sort) =>
  !sort || !sort.key || sort.key === DEFAULT_SORT ? '' : `${sort.key}|${sort.dir || DEFAULT_DIR}`;

export const parse_sort_option = (value) => {
  const [key, dir] = String(value || '').split('|');
  if (!key) return { key: DEFAULT_SORT, dir: DEFAULT_DIR };
  return { key, dir: dir === 'asc' ? 'asc' : 'desc' };
};

// The issue list's Status choices; '' is the server default (open + acknowledged).
export const ISSUE_STATUS_OPTIONS = ISSUE_STATUSES.map((s) => ({
  value: s,
  label: s.charAt(0) + s.slice(1).toLowerCase().replace('_', '-')
})).concat([{ value: DECIDED_STATUS_FILTER, label: DECIDED_STATUS_LABEL }]);
export const DEFAULT_STATUS_LABEL = 'Open + acknowledged';

// Status and teacher are the list's filters; tier and category are where it was opened from.
export const issue_filters_on = ({ status, teacher_uuid }) => !!(status || teacher_uuid);

const issue_status_words = (status) => {
  const o = ISSUE_STATUS_OPTIONS.find((x) => x.value === status);
  return (o ? o.label : DEFAULT_STATUS_LABEL).toLowerCase();
};

// The sentence under the issue filters, the Overview's shape. Null while loading.
export const issue_filter_summary = ({
  total,
  month_label: month,
  scope_label,
  status,
  teacher_uuid,
  teacher_name,
  loading
}) => {
  if (loading) return null;
  const noun = total === 1 ? 'issue' : 'issues';
  const scope = scope_label || 'every tier';
  if (!issue_filters_on({ status, teacher_uuid })) {
    return {
      lead: `all ${total} ${noun}`,
      rest: ` for ${month}, ${scope}, ${issue_status_words('')}.`
    };
  }
  const parts = [scope];
  if (teacher_uuid) parts.push(teacher_name ? `teacher ${teacher_name}` : 'one teacher');
  parts.push(issue_status_words(status));
  return { lead: `${total} ${noun}`, rest: ` for ${month}: ${parts.join(', ')}.` };
};
